import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type {
  Contract,
  ContractDocument,
  ContractParty,
  ContractStatus,
  ContractTract,
  ContractType,
} from '../types/api';

export const contractsKeys = {
  all: ['contracts'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...contractsKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...contractsKeys.all, 'byId', projectId, id] as const,
  tracts: (projectId: string | null, contractId: string) =>
    [...contractsKeys.all, 'tracts', projectId, contractId] as const,
  parties: (projectId: string | null, contractId: string) =>
    [...contractsKeys.all, 'parties', projectId, contractId] as const,
  documents: (projectId: string | null, contractId: string) =>
    [...contractsKeys.all, 'documents', projectId, contractId] as const,
};

export interface ListFilters {
  q?: string | undefined;
  contract_type?: ContractType | undefined;
  status?: ContractStatus | undefined;
  recorded?: boolean | undefined;
  expiring_before?: string | undefined;
  tract_id?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useContractsList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: contractsKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contracts: Contract[] }>('/contracts', { ...filters });
      return res.contracts;
    },
  });
}

export function useContract(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? contractsKeys.byId(projectId, id) : ['contracts', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contract: Contract }>(`/contracts/${id}`);
      return res.contract;
    },
  });
}

export interface ContractInput {
  contract_type: ContractType;
  status?: ContractStatus | undefined;
  title: string;
  contract_number?: string | null | undefined;
  effective_date?: string | null | undefined;
  execution_date?: string | null | undefined;
  expiration_date?: string | null | undefined;
  termination_date?: string | null | undefined;
  renewal_notice_date?: string | null | undefined;
  recorded?: boolean | undefined;
  recording_state?: string | null | undefined;
  recording_county?: string | null | undefined;
  recording_volume?: string | null | undefined;
  recording_page?: string | null | undefined;
  recording_instrument_number?: string | null | undefined;
  recording_date?: string | null | undefined;
  consideration_amount?: number | string | null | undefined;
  consideration_currency?: string | null | undefined;
  consideration_notes?: string | null | undefined;
  row_use?: string | null | undefined;
  row_width_feet?: number | string | null | undefined;
  row_length_feet?: number | string | null | undefined;
  row_length_rods?: number | string | null | undefined;
  row_pipeline_diameter_inches?: number | string | null | undefined;
  row_pipeline_product?: string | null | undefined;
  row_depth_restriction_feet?: number | string | null | undefined;
  row_surface_restoration_required?: boolean | null | undefined;
  description?: string | null | undefined;
  notes?: string | null | undefined;
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractInput) => api.post<{ contract: Contract }>('/contracts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

export function useUpdateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ContractInput> }) =>
      api.patch<{ contract: Contract }>(`/contracts/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

export function useDeleteContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contracts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// contract_tracts
// ---------------------------------------------------------------------------
export function useContractTractsList(contractId: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: contractId ? contractsKeys.tracts(projectId, contractId) : ['contract-tracts', 'none'],
    enabled: !!contractId && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contract_tracts: ContractTract[] }>('/contract-tracts', {
        contract_id: contractId!,
      });
      return res.contract_tracts;
    },
  });
}

export interface ContractTractInput {
  contract_id: string;
  tract_id: string;
  length_feet?: number | null;
  length_rods?: number | null;
  width_feet?: number | null;
  crossing_order?: number;
  notes?: string | null;
}

export function useAttachContractTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractTractInput) =>
      api.post<{ contract_tract: ContractTract }>('/contract-tracts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

export function useUpdateContractTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Omit<ContractTractInput, 'contract_id' | 'tract_id'>> }) =>
      api.patch<{ contract_tract: ContractTract }>(`/contract-tracts/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

export function useDetachContractTract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contract-tracts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// contract_parties
// ---------------------------------------------------------------------------
export function useContractPartiesList(contractId: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: contractId
      ? contractsKeys.parties(projectId, contractId)
      : ['contract-parties', 'none'],
    enabled: !!contractId && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contract_parties: ContractParty[] }>('/contract-parties', {
        contract_id: contractId!,
      });
      return res.contract_parties;
    },
  });
}

export interface ContractPartyInput {
  contract_id: string;
  party_id: string;
  role: 'grantor' | 'grantee' | 'other';
  party_order?: number;
  note?: string | null;
}

export function useAttachContractParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractPartyInput) =>
      api.post<{ contract_party: ContractParty }>('/contract-parties', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

export function useDetachContractParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contract-parties/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// contract_documents
// ---------------------------------------------------------------------------
export function useContractDocumentsList(contractId: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: contractId
      ? contractsKeys.documents(projectId, contractId)
      : ['contract-documents', 'none'],
    enabled: !!contractId && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contract_documents: ContractDocument[] }>('/contract-documents', {
        contract_id: contractId!,
      });
      return res.contract_documents;
    },
  });
}

export interface ContractDocumentInput {
  contract_id: string;
  document_id: string;
  page_start?: number | null;
  page_end?: number | null;
  note?: string | null;
}

export function useAttachContractDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractDocumentInput) =>
      api.post<{ contract_document: ContractDocument }>('/contract-documents', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}

export function useDetachContractDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contract-documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsKeys.all }),
  });
}
